import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { network } from "@/config";
import { toLocaleString } from "@/lib/utils";
import { DSContractListing } from "@myth-finance/dualstake-ts-sdk";
import { useQuery } from "@tanstack/react-query";
import { getApplicationAddress } from "algosdk";
import { Box } from "lucide-react";
import { useMemo } from "react";
import { WithErrorAndLoading } from "./with-error-and-loading";
import { Loading } from "./loading";
import { prettyTimeframe } from "@/pages/dualstake/components/swaps-card";
import { useTimeframeTabContext } from "@/providers/timeframe-tab-context";

interface ProducedBlock {
  rnd: number;
  ts: number;
}

const NetworkAPIs: Record<typeof network, string> = {
  fnet: "https://fnet-analytics.d13.co",
  mainnet: "https://mainnet-analytics.d13.co",
};

const DAY = 86400;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;

export function BlocksProposedCard({
  listing,
}: {
  listing: DSContractListing;
}) {
  const { activeTab, setActiveTab } = useTimeframeTabContext();
  const appEscrow = useMemo(
    () => (listing?.appId ? getApplicationAddress(listing.appId) : ""),
    [listing]
  );

  const { isPending, error, data, refetch } = useQuery({
    queryKey: ["blocksProduced", appEscrow],
    queryFn: async (): Promise<ProducedBlock[] | null> => {
      if (!NetworkAPIs[network]) return null;
      if (!appEscrow) return null;
      const response = await fetch(
        `${NetworkAPIs[network]}/v0/proposer/${appEscrow}`
      );
      if (!response.ok) {
        try {
          throw new Error(await response.json());
        } catch (e) {
          throw new Error(await response.text());
        }
      }
      return response.json();
    },
  });

  const blocks = useMemo(() => {
    if (!data) return null;
    const blockDiffs = data.map(
      ({ ts }) => Math.floor(+new Date() / 1000) - ts
    );
    return {
      "1D": {
        label: "24 hours",
        count: blockDiffs.filter((bd) => bd <= DAY).length,
      },
      "1W": {
        label: "7 days",
        count: blockDiffs.filter((bd) => bd <= WEEK).length,
      },
      "1M": {
        label: "30 days",
        count: blockDiffs.filter((bd) => bd <= MONTH).length,
      },
      ALL: { label: "nomatter", count: blockDiffs.length },
    };
  }, [data]);

  if (!NetworkAPIs[network]) return null;

  return (
    <Card className="min-w-[300px] relative self-stretch">
      <CardHeader>
        <CardTitle className="opacity-0 animate-fade-in-3">
          <Box className="relative -top-[2px] inline" /> Blocks Proposed
        </CardTitle>
      </CardHeader>
      <CardContent className="opacity-0 animate-fade-in-3">
        <WithErrorAndLoading
          mini={true}
          loading={false}
          error={error?.message}
          retry={refetch}
        >
          {blocks || isPending ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full mb-3 -mt-2 grid-cols-4">
                <TabsTrigger value="1D">24H</TabsTrigger>
                <TabsTrigger value="1W">1W</TabsTrigger>
                <TabsTrigger value="1M">1M</TabsTrigger>
                <TabsTrigger value="ALL">ALL</TabsTrigger>
              </TabsList>
              {Object.entries(blocks ?? {}).map(
                ([timeframe, { label, count }]) => (
                  <TabsContent key={timeframe} value={timeframe}>
                    <div className="text-3xl font-bold">
                      {toLocaleString(count)}
                    </div>
                    <p className="text-muted-foreground">
                      {timeframe === "ALL"
                        ? "Lifetime blocks"
                        : `blocks in the past ${prettyTimeframe(label)}`}
                    </p>
                  </TabsContent>
                )
              )}
            </Tabs>
          ) : null}
          {!blocks ? (
            isPending ? (
              <Loading className="mt-8 mb-4" noLogo={true} />
            ) : (
              <div className="text-center">N/A</div>
            )
          ) : null}
        </WithErrorAndLoading>
      </CardContent>
      <Box
        size={192}
        strokeWidth="1.25px"
        className="absolute pointer-events-none z-0 top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] opacity-5"
      />
    </Card>
  );
}